/**
 * Why Cognidoor V4 — Signature Showcase.
 * Canonical source: Desktop cognidoor-variants-preview Variant 4 · Signature Showcase.
 * Copy, structure, and styling preserved verbatim from preview.
 */
import React, { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { useTrack } from "../../contexts/TrackContext";
import { useIsMobile } from "../../hooks/useIsMobile";

type Track = "all" | "mck" | "bcg";

const NAV_HEIGHT_PX = 58;
/** When expanded, we keep the same left edge as a 1440px-centered block and extend right to nav edge. */
const EXPANDED_BASE_WIDTH_PX = 1440;
/** Compact = same right edge, width shrinks by 8% (sliding door: left edge moves right 8%). */
const COMPACT_SHRINK_RATIO = 0.92;
/** Sidebar column width (lg+) + grid gap so we can compute second column width. */
const SIDEBAR_COLUMN_PX = 380;
const GRID_GAP_PX = 48;
/** Tailwind lg — sticky sidebar + snap layout (was xl-only; clip-path on section also broke sticky until noClipBottom). */
const SIDEBAR_BREAKPOINT_PX = 1024;

const base = process.env.PUBLIC_URL || "";
const seawolfDemoImg = `${base}/images/landing/seawolf-demo.png`;
const caseyDemoImg = `${base}/images/landing/casey-demo.png`;
const redrockDemoImg = `${base}/images/landing/redrock-demo.png`;

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const gradientButtonClass =
  "inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-400 px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:shadow-md";
const ghostButtonClass =
  "inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/8 px-5 py-3 text-sm font-semibold text-white/90 backdrop-blur transition hover:bg-white/12";

function GradientButton({
  children,
  to,
}: {
  children: React.ReactNode;
  to?: string;
}) {
  if (to) {
    return (
      <Link to={to} className={gradientButtonClass}>
        {children}
      </Link>
    );
  }
  return <button className={gradientButtonClass}>{children}</button>;
}

function GhostButton({
  children,
  to,
}: {
  children: React.ReactNode;
  to?: string;
}) {
  if (to) {
    return (
      <Link to={to} className={ghostButtonClass}>
        {children}
      </Link>
    );
  }
  return <button className={ghostButtonClass}>{children}</button>;
}

/** Track-themed section background: same palette as Hero (LandingCanonical) for consistency. */
function getSectionShellBg(track: Track | undefined): string {
  if (track === "mck") {
    return "bg-[linear-gradient(135deg,var(--cog-blue-1)_0%,var(--cog-blue-2)_55%,var(--cog-blue-3)_100%)]";
  }
  if (track === "bcg") {
    return "bg-[linear-gradient(135deg,var(--cog-blue-1)_0%,#10b981_40%,var(--cog-blue-2)_100%)]";
  }
  return "bg-[radial-gradient(circle_at_top_left,rgba(36,123,179,0.28),transparent_32%),radial-gradient(circle_at_top_right,rgba(0,204,153,0.16),transparent_22%),linear-gradient(180deg,#061225_0%,#09172f_55%,#071222_100%)]";
}

const SectionShell = React.forwardRef<
  HTMLElement,
  { children: React.ReactNode; dark?: boolean; track?: Track; /** When true, skip bottom clip so content (e.g. mobile step cards) is not cut off. */ noClipBottom?: boolean }
>(function SectionShell({ children, dark = true, track, noClipBottom = false }, ref) {
  const isDark = dark;
  // Only apply track-themed background when McKinsey or BCG; "all" keeps original styling (no track prop effect).
  const trackThemed = track === "mck" || track === "bcg";
  const trackBg = isDark && trackThemed ? getSectionShellBg(track) : null;
  return (
    <section
      ref={ref}
      className={cx(
        "relative overflow-visible border shadow-[0_30px_90px_-45px_rgba(2,6,23,0.65)]",
        isDark
          ? cx(
              "border-white/10 text-white",
              trackBg ?? "bg-[radial-gradient(circle_at_top_left,rgba(36,123,179,0.28),transparent_32%),radial-gradient(circle_at_top_right,rgba(0,204,153,0.16),transparent_22%),linear-gradient(180deg,#061225_0%,#09172f_55%,#071222_100%)]"
            )
          : "border-slate-200 bg-white text-slate-900"
      )}
      style={
        isDark && !noClipBottom
          ? { clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 5rem), 0 100%)" }
          : undefined
      }
    >
      {/* Overlay only for track-themed (McKinsey/BCG); "all" keeps original look. */}
      {isDark && trackThemed && (
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.2),transparent_45%),radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.12),transparent_45%)]" />
      )}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_10%,rgba(255,255,255,0.08),transparent_26%),radial-gradient(circle_at_85%_20%,rgba(255,255,255,0.05),transparent_24%)]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:20px_20px]" />
      <div className="relative">{children}</div>
    </section>
  );
});

function MockWindow({
  title,
  subtitle,
  titleNode,
  children,
  contentClassName,
}: {
  title: string;
  subtitle: string;
  /** When provided, used instead of title/subtitle for smooth slide-specific headers */
  titleNode?: React.ReactNode;
  children: React.ReactNode;
  /** Optional class for the content wrapper (default p-4) */
  contentClassName?: string;
}) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-white/10 bg-[#071224]/80 shadow-[0_30px_60px_-35px_rgba(2,6,23,0.9)] backdrop-blur">
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
        <div className="min-w-0 flex-1">
          {titleNode ?? (
            <>
              <div className="text-sm font-semibold text-white/95">{title}</div>
              <div className="mt-1 text-xs text-white/55">{subtitle}</div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-sky-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/35" />
        </div>
      </div>
      <div className={cx("min-w-0", contentClassName ?? "p-4")}>{children}</div>
    </div>
  );
}

const DEMO_SLIDES = [
  {
    src: seawolfDemoImg,
    alt: "Sea Wolf practice — coach mode and score breakdown",
    title: "McKinsey Sea Wolf Simulation",
    subtitle: "Coach mode · Study guide · score breakdown",
  },
  {
    src: caseyDemoImg,
    alt: "BCG Casey practice — questions, exhibits, and feedback",
    title: "BCG Casey Practice",
    subtitle: "Guided mode · timed questions · final debrief",
  },
  {
    src: redrockDemoImg,
    alt: "Red Rock simulation — investigation, exhibits, journal, coach feedback",
    title: "McKinsey Red Rock simulation",
    subtitle: "Investigation · Exhibits · Journal · Coach breakdown",
  },
];

/** Coach feedback screenshots: McKinsey = 0 (Seawolf), BCG = 1 (Casey), McKinsey = 2 (Red Rock). */
const COACH_FEEDBACK_SLIDES = [
  {
    src: "/images/landing/seawolf-coach-feedback.png",
    alt: "Mckinsey Seawolf Coach Feedback",
    title: "Mckinsey Seawolf Coach Feedback",
    subtitle: "Treatment effectiveness · coach view",
  },
  {
    src: "/images/landing/casey-coach-feedback.png",
    alt: "BCG Casey Coach Feedback",
    title: "BCG Casey Coach Feedback",
    subtitle: "Coach summary · executive debrief",
  },
  {
    src: "/images/landing/redrock-coach-feedback.png",
    alt: "McKinsey Red Rock Run review",
    title: "McKinsey Red Rock Run review",
    subtitle: "Run review · strengths · next steps",
  },
];

/** AI Cases library screenshots: McKinsey (purple) and BCG (green), same sync order. */
const AI_CASES_SLIDES = [
  {
    src: `${base}/images/landing/ai-cases-mckinsey.png`,
    alt: "McKinsey Lilli Interview AI Coach",
    title: "McKinsey Lilli Interview AI Coach",
    subtitle: "Case library · Guided · Browse",
  },
  {
    src: `${base}/images/landing/ai-cases-bcg.png`,
    alt: "BCG Candidate Interview AI Coach",
    title: "BCG Candidate Interview AI Coach",
    subtitle: "Case library · Guided · Browse",
  },
];

/** McKinsey track only: two-up carousel (library + live session), synced to global demoIndex like other mocks. */
const AI_CASES_MCKINSEY_PREVIEW_SLIDES = [
  AI_CASES_SLIDES[0],
  {
    src: `${base}/images/landing/ai-cases-mckinsey-war-room.png`,
    alt: "McKinsey AI Coach — live case with interviewer and coach feedback",
    title: "McKinsey Lilli Interview AI Coach",
    subtitle: "Live session · Interviewer + coach sidebar",
  },
];

/** Dashboard snapshot screenshots: AI Coach Snapshot (getting started + building momentum). */
const DASHBOARD_SNAPSHOT_SLIDES = [
  {
    src: "/images/landing/dashboard-snapshot-1.png",
    alt: "AI Coach Snapshot — Just getting started",
    title: "AI Coach Snapshot",
    subtitle: "Readiness · skill trends · what to focus on next",
  },
  {
    src: "/images/landing/dashboard-snapshot-2.png",
    alt: "AI Coach Snapshot — Building momentum",
    title: "AI Coach Snapshot",
    subtitle: "Readiness · skill trends · what to focus on next",
  },
];

const CAROUSEL_INTERVAL_MS = 6000;
const SLIDE_DURATION_MS = 500;
const TITLE_TRANSITION_MS = 500;

/** All track: 3 slides (Sea Wolf, Casey, Red Rock). McKinsey: 2 (Sea Wolf, Red Rock). BCG: 1 (Casey). */
const CAROUSEL_LENGTH_ALL = 3;
const CAROUSEL_LENGTH_MCK = 2;
/** Used by interval and other mocks (Casey, Dashboard, etc.) — 3 positions. */
const CAROUSEL_LENGTH = 3;

function SeaWolfMock({ index, track }: { index: number; track: Track }) {
  const prevIndexRef = useRef(index);
  const [noTransition, setNoTransition] = useState(false);
  const mckOnly = track === "mck";
  const bcgOnly = track === "bcg";
  const carouselLength = mckOnly ? CAROUSEL_LENGTH_MCK : bcgOnly ? 1 : CAROUSEL_LENGTH_ALL;
  /** For "all": index 0,1,2. For "mck": index 0,1 (Sea Wolf, Red Rock). For "bcg": index 0. */
  const effectiveIndex = mckOnly ? index % CAROUSEL_LENGTH_MCK : bcgOnly ? 0 : index % CAROUSEL_LENGTH_ALL;

  useEffect(() => {
    if (bcgOnly) return;
    if (effectiveIndex === 0 && prevIndexRef.current === carouselLength - 1) {
      setNoTransition(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setNoTransition(false));
      });
      return () => cancelAnimationFrame(id);
    }
    prevIndexRef.current = effectiveIndex;
  }, [effectiveIndex, carouselLength, bcgOnly]);

  /** Slide indices: all = [0,1,2], mck = [0,2] (Sea Wolf, Red Rock), bcg = [1]. */
  const slideIndices = mckOnly ? [0, 2] : bcgOnly ? [1] : [0, 1, 2];
  const slidesToShow = slideIndices.map((i) => DEMO_SLIDES[i]);
  const currentSlide = slidesToShow[effectiveIndex];
  const titleNode = (
    <div className="relative min-h-[2.5rem]">
      {slidesToShow.map((slide, i) => (
        <div
          key={slide.title}
          className="absolute inset-0 transition-opacity ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{
            opacity: effectiveIndex === i ? 1 : 0,
            transitionDuration: `${TITLE_TRANSITION_MS}ms`,
          }}
        >
          <div className="text-sm font-semibold text-white/95">{slide.title}</div>
          <div className="mt-1 text-xs text-white/55">{slide.subtitle}</div>
        </div>
      ))}
    </div>
  );

  return (
    <MockWindow
      title={currentSlide.title}
      subtitle={currentSlide.subtitle}
      titleNode={titleNode}
    >
      <div className="min-w-0 max-w-full overflow-hidden rounded-[22px] bg-[#071224] aspect-video min-h-[280px] relative">
        <div
          className="flex h-full ease-[cubic-bezier(0.32,0.72,0,1)]"
          style={{
            width: carouselLength === 1 ? "100%" : `${carouselLength * 100}%`,
            transform: carouselLength === 1 ? "none" : `translateX(-${effectiveIndex * (100 / carouselLength)}%)`,
            transition: noTransition ? "none" : `transform ${SLIDE_DURATION_MS}ms cubic-bezier(0.32,0.72,0,1)`,
          }}
        >
          {slidesToShow.map((slide, i) => (
            <div
              key={i}
              className="flex min-w-0 h-full flex-shrink-0 items-center justify-center bg-[#0d1424]"
              style={{ width: carouselLength === 1 ? "100%" : `${100 / carouselLength}%` }}
            >
              <img
                src={slide.src}
                alt={slide.alt}
                className="max-h-full max-w-full w-auto h-auto object-contain object-center"
              />
            </div>
          ))}
        </div>
      </div>
    </MockWindow>
  );
}

function CaseyMock({ index, track }: { index: number; track: Track }) {
  const prevIndexRef = useRef(index);
  const [noTransition, setNoTransition] = useState(false);
  const mckOnly = track === "mck";
  const bcgOnly = track === "bcg";
  const carouselLength = mckOnly ? CAROUSEL_LENGTH_MCK : bcgOnly ? 1 : CAROUSEL_LENGTH_ALL;
  /** For "all": index 0,1,2. For "mck": index 0,1 (Seawolf, Red Rock). For "bcg": index 0. */
  const effectiveIndex = mckOnly ? index % CAROUSEL_LENGTH_MCK : bcgOnly ? 0 : index % CAROUSEL_LENGTH_ALL;

  useEffect(() => {
    if (bcgOnly) return;
    if (effectiveIndex === 0 && prevIndexRef.current === carouselLength - 1) {
      setNoTransition(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setNoTransition(false));
      });
      return () => cancelAnimationFrame(id);
    }
    prevIndexRef.current = effectiveIndex;
  }, [effectiveIndex, carouselLength, bcgOnly]);

  /** Slide indices: all = [0,1,2], mck = [0,2] (Seawolf, Red Rock), bcg = [1]. */
  const slideIndices = mckOnly ? [0, 2] : bcgOnly ? [1] : [0, 1, 2];
  const slidesToShow = slideIndices.map((i) => COACH_FEEDBACK_SLIDES[i]);
  const currentSlide = slidesToShow[effectiveIndex];
  const titleNode = (
    <div className="relative min-h-[2.5rem]">
      {slidesToShow.map((s, i) => (
        <div
          key={s.title}
          className="absolute inset-0 transition-opacity ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{
            opacity: effectiveIndex === i ? 1 : 0,
            transitionDuration: `${TITLE_TRANSITION_MS}ms`,
          }}
        >
          <div className="text-sm font-semibold text-white/95">{s.title}</div>
          <div className="mt-1 text-xs text-white/55">{s.subtitle}</div>
        </div>
      ))}
    </div>
  );

  return (
    <MockWindow
      title={currentSlide.title}
      subtitle={currentSlide.subtitle}
      titleNode={titleNode}
    >
      <div className="overflow-hidden rounded-[16px] bg-[#071224] aspect-[792/837] min-h-[280px] relative">
        <div
          className="flex h-full ease-[cubic-bezier(0.32,0.72,0,1)]"
          style={{
            width: carouselLength === 1 ? "100%" : `${carouselLength * 100}%`,
            transform: carouselLength === 1 ? "none" : `translateX(-${effectiveIndex * (100 / carouselLength)}%)`,
            transition: noTransition ? "none" : `transform ${SLIDE_DURATION_MS}ms cubic-bezier(0.32,0.72,0,1)`,
          }}
        >
          {slidesToShow.map((s, i) => (
            <div
              key={i}
              className="flex h-full flex-shrink-0 items-center justify-center bg-[#0d1424]"
              style={{ width: carouselLength === 1 ? "100%" : `${100 / carouselLength}%` }}
            >
              <img
                src={s.src}
                alt={s.alt}
                className="h-full w-full object-contain object-center"
              />
            </div>
          ))}
        </div>
      </div>
    </MockWindow>
  );
}

function DashboardMock({ index = 0, track }: { index?: number; track: Track }) {
  const prevIndexRef = useRef(index);
  const [noTransition, setNoTransition] = useState(false);
  const mckDualCarousel = track === "mck";
  const bcgOnlySingle = track === "bcg";

  const mckEffectiveIndex = index % 2;
  const allContentIndex = index === 2 ? 0 : index;

  const carouselLength = mckDualCarousel ? 2 : bcgOnlySingle ? 1 : CAROUSEL_LENGTH;
  const slideTranslateIndex = mckDualCarousel ? mckEffectiveIndex : bcgOnlySingle ? 0 : index;

  useEffect(() => {
    if (bcgOnlySingle) return;
    if (mckDualCarousel) {
      if (mckEffectiveIndex === 0 && prevIndexRef.current === 1) {
        setNoTransition(true);
        const id = requestAnimationFrame(() => {
          requestAnimationFrame(() => setNoTransition(false));
        });
        return () => cancelAnimationFrame(id);
      }
      prevIndexRef.current = mckEffectiveIndex;
      return;
    }
    if (index === 0 && prevIndexRef.current === 2) {
      setNoTransition(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setNoTransition(false));
      });
      return () => cancelAnimationFrame(id);
    }
    prevIndexRef.current = index;
  }, [index, mckEffectiveIndex, mckDualCarousel, bcgOnlySingle]);

  const bcgSlide = AI_CASES_SLIDES[1];
  const currentSlide = mckDualCarousel
    ? AI_CASES_MCKINSEY_PREVIEW_SLIDES[mckEffectiveIndex]
    : bcgOnlySingle
      ? bcgSlide
      : AI_CASES_SLIDES[allContentIndex];

  const slidesToShow = mckDualCarousel
    ? AI_CASES_MCKINSEY_PREVIEW_SLIDES
    : bcgOnlySingle
      ? [bcgSlide]
      : [AI_CASES_SLIDES[0], AI_CASES_SLIDES[1], AI_CASES_SLIDES[0]];

  const titleNode = (
    <div className="relative min-h-[2.5rem]">
      {(mckDualCarousel ? AI_CASES_MCKINSEY_PREVIEW_SLIDES : bcgOnlySingle ? [bcgSlide] : AI_CASES_SLIDES).map((s, i) => (
        <div
          key={`${s.subtitle}-${i}`}
          className="absolute inset-0 transition-opacity ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{
            opacity:
              mckDualCarousel
                ? mckEffectiveIndex === i
                  ? 1
                  : 0
                : bcgOnlySingle
                  ? 1
                  : allContentIndex === i
                    ? 1
                    : 0,
            transitionDuration: `${TITLE_TRANSITION_MS}ms`,
          }}
        >
          <div className="text-sm font-semibold text-white/95">{s.title}</div>
          <div className="mt-1 text-xs text-white/55">{s.subtitle}</div>
        </div>
      ))}
    </div>
  );

  return (
    <MockWindow
      title={currentSlide.title}
      subtitle={currentSlide.subtitle}
      titleNode={titleNode}
      contentClassName="p-4"
    >
      <div className="min-w-0 max-w-full overflow-hidden rounded-[16px] bg-[#071224] aspect-video max-h-[560px] min-h-[220px] sm:min-h-[360px] relative">
        <div
          className="flex h-full ease-[cubic-bezier(0.32,0.72,0,1)]"
          style={{
            width: carouselLength === 1 ? "100%" : `${carouselLength * 100}%`,
            transform: carouselLength === 1 ? "none" : `translateX(-${slideTranslateIndex * (100 / carouselLength)}%)`,
            transition: noTransition ? "none" : `transform ${SLIDE_DURATION_MS}ms cubic-bezier(0.32,0.72,0,1)`,
          }}
        >
          {slidesToShow.map((s, i) => (
            <div
              key={i}
              className="flex min-w-0 h-full flex-shrink-0 items-center justify-center bg-[#0d1424]"
              style={{ width: carouselLength === 1 ? "100%" : `${100 / carouselLength}%` }}
            >
              <div className="flex min-w-0 h-full w-full items-center justify-center pl-6">
                <img
                  src={s.src}
                  alt={s.alt}
                  className="max-h-full max-w-full w-auto h-auto object-contain object-center"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </MockWindow>
  );
}

/**
 * Step 4: Same two dashboard snapshot images and sliding carousel for All, McKinsey, and BCG.
 * Intentionally does not receive track — content and behavior must stay identical across every track.
 */
function DashboardSnapshotMock({ index = 0 }: { index?: number }) {
  const prevIndexRef = useRef(index);
  const [noTransition, setNoTransition] = useState(false);

  useEffect(() => {
    if (index === 0 && prevIndexRef.current === 2) {
      setNoTransition(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setNoTransition(false));
      });
      return () => cancelAnimationFrame(id);
    }
    prevIndexRef.current = index;
  }, [index]);

  const contentIndex = index === 2 ? 0 : index;
  const slide = DASHBOARD_SNAPSHOT_SLIDES[contentIndex];
  const titleNode = (
    <div className="relative min-h-[2.5rem]">
      {DASHBOARD_SNAPSHOT_SLIDES.map((s, i) => (
        <div
          key={`${s.alt}-${i}`}
          className="absolute inset-0 transition-opacity ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{
            opacity: contentIndex === i ? 1 : 0,
            transitionDuration: `${TITLE_TRANSITION_MS}ms`,
          }}
        >
          <div className="text-sm font-semibold text-white/95">{s.title}</div>
          <div className="mt-1 text-xs text-white/55">{s.subtitle}</div>
        </div>
      ))}
    </div>
  );

  const trackSlides = [DASHBOARD_SNAPSHOT_SLIDES[0], DASHBOARD_SNAPSHOT_SLIDES[1], DASHBOARD_SNAPSHOT_SLIDES[0]];

  return (
    <MockWindow
      title={slide.title}
      subtitle={slide.subtitle}
      titleNode={titleNode}
      contentClassName="p-4"
    >
      <div className="min-w-0 max-w-full overflow-hidden rounded-[16px] bg-[#071224] aspect-video max-h-[560px] min-h-[220px] sm:min-h-[360px] relative">
        <div
          className="flex h-full ease-[cubic-bezier(0.32,0.72,0,1)]"
          style={{
            width: `${CAROUSEL_LENGTH * 100}%`,
            transform: `translateX(-${index * (100 / CAROUSEL_LENGTH)}%)`,
            transition: noTransition ? "none" : `transform ${SLIDE_DURATION_MS}ms cubic-bezier(0.32,0.72,0,1)`,
          }}
        >
          {trackSlides.map((s, i) => (
            <div
              key={i}
              className="flex min-w-0 h-full flex-shrink-0 items-center justify-center bg-[#0d1424]"
              style={{ width: `${100 / CAROUSEL_LENGTH}%` }}
            >
              <img
                src={s.src}
                alt={s.alt}
                className="max-h-full max-w-full w-auto h-auto object-contain object-center"
              />
            </div>
          ))}
        </div>
      </div>
    </MockWindow>
  );
}

/** Card indices to show per track: all four steps for every track; Step 2 shows McKinsey or BCG coach feedback per track. */
const CARD_INDICES_BY_TRACK: Record<Track, number[]> = {
  all: [0, 1, 2, 3],
  mck: [0, 1, 2, 3],
  bcg: [0, 1, 2, 3],
};

export default function WhyCognidoorV4() {
  const { track: trackFromCtx } = useTrack();
  const track = (trackFromCtx === "mck" || trackFromCtx === "bcg" ? trackFromCtx : "all") as Track;
  const location = useLocation();
  const isMobile = useIsMobile(767);
  const isPricingTarget =
    location.hash === "#pricing" ||
    (location.state as { scrollToPricing?: boolean } | null)?.scrollToPricing === true;
  const [demoIndex, setDemoIndex] = useState(0);
  const stepCardsGridRef = useRef<HTMLDivElement>(null);
  const step1CardRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const cardsColumnParentRef = useRef<HTMLDivElement | null>(null);
  const [bottomSpacerMinHeight, setBottomSpacerMinHeight] = useState(190);
  const [sidebarMode, setSidebarMode] = useState(false);
  const [isXl, setIsXl] = useState(
    typeof window !== "undefined" ? window.matchMedia(`(min-width: ${SIDEBAR_BREAKPOINT_PX}px)`).matches : false
  );
  const [expandedLayoutPx, setExpandedLayoutPx] = useState<{
    expandedMarginLeft: number;
    expandedMaxWidth: number;
    compactMarginLeft: number;
    compactMaxWidth: number;
  }>({
    expandedMarginLeft: 0,
    expandedMaxWidth: EXPANDED_BASE_WIDTH_PX,
    compactMarginLeft: 0,
    compactMaxWidth: EXPANDED_BASE_WIDTH_PX * COMPACT_SHRINK_RATIO,
  });

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${SIDEBAR_BREAKPOINT_PX}px)`);
    const fn = () => setIsXl(mq.matches);
    fn();
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  // Expanded: left edge where 1440px-centered block would be, right edge at parent (nav). Compact: same right edge (right-aligned in second column), width 92%. Skip on mobile.
  useEffect(() => {
    if (isMobile) return;
    const el = cardsColumnParentRef.current;
    if (!el) return;
    const update = () => {
      const parentWidth = el.getBoundingClientRect().width;
      const expandedMarginLeft = Math.max(0, (parentWidth - EXPANDED_BASE_WIDTH_PX) / 2);
      const expandedMaxWidth = parentWidth - expandedMarginLeft;
      const compactMaxWidth = expandedMaxWidth * COMPACT_SHRINK_RATIO;
      const column2Width = parentWidth - SIDEBAR_COLUMN_PX - GRID_GAP_PX;
      const compactMarginLeft = Math.max(0, column2Width - compactMaxWidth);
      setExpandedLayoutPx((prev) =>
        prev.expandedMarginLeft === expandedMarginLeft &&
        prev.expandedMaxWidth === expandedMaxWidth &&
        prev.compactMarginLeft === compactMarginLeft &&
        prev.compactMaxWidth === compactMaxWidth
          ? prev
          : { expandedMarginLeft, expandedMaxWidth, compactMarginLeft, compactMaxWidth }
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile]);

  const showSidebar = sidebarMode && isXl;
  const [showSidebarHeadline, setShowSidebarHeadline] = useState(false);
  const [exitDelayShrink, setExitDelayShrink] = useState(false);
  const prevShowSidebarRef = useRef(showSidebar);
  /** When entering sidebar, we store Step 2's viewport top so we can restore scroll and avoid jumping into Step 3. */
  const anchorViewportTopRef = useRef<number | null>(null);

  // Stagger enter: container shrinks first, then sidebar headline fades in (gradual).
  useEffect(() => {
    if (showSidebar) {
      const t = setTimeout(() => setShowSidebarHeadline(true), 420);
      return () => clearTimeout(t);
    }
    setShowSidebarHeadline(false);
  }, [showSidebar]);

  // When sidebar just opened: restore scroll so Step 2 (or whatever was in view) stays in the same place; prevents jump to Step 3.
  // Do not restore when user is targeting #pricing (nav/CTA), or we would steal the viewport back to the Why section.
  useEffect(() => {
    if (!showSidebar || anchorViewportTopRef.current == null || isPricingTarget) return;
    const step2El = stepCardsGridRef.current?.querySelector<HTMLElement>("[data-step-number='2']");
    if (!step2El) {
      anchorViewportTopRef.current = null;
      return;
    }
    const applyScroll = () => {
      const r = step2El.getBoundingClientRect();
      const targetScrollY = r.top + window.scrollY - anchorViewportTopRef.current!;
      window.scrollTo(0, Math.max(0, targetScrollY));
      anchorViewportTopRef.current = null;
    };
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(applyScroll);
    });
    return () => cancelAnimationFrame(rafId);
  }, [showSidebar, isPricingTarget]);

  // Stagger exit: when scrolling up, first show top headline (fade in), then expand container after delay.
  useEffect(() => {
    const wasOpen = prevShowSidebarRef.current;
    prevShowSidebarRef.current = showSidebar;
    if (wasOpen && !showSidebar) {
      setExitDelayShrink(true);
      const t = setTimeout(() => setExitDelayShrink(false), 480);
      return () => clearTimeout(t);
    }
  }, [showSidebar]);

  // Enter sidebar when Step 2 container's top border touches the nav (smoother transition); leave when user scrolls back to bottom of "Why Cognidoor?" section above.
  // Do not enter sidebar when URL hash is #pricing (user clicked Pricing in nav/CTA); otherwise we'd open sidebar and restore-scroll would steal the viewport.
  // Only run on desktop/tablet (md and up); on mobile we use static layout with no sidebar.
  useEffect(() => {
    if (isMobile) return;
    const WHY_SECTION_ID = "why-cognidoor-section";
    let rafId: number | null = null;
    const check = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const step2El = stepCardsGridRef.current?.querySelector<HTMLElement>("[data-step-number='2']");
        const whySectionEl = typeof document !== "undefined" ? document.getElementById(WHY_SECTION_ID) : null;
        if (!step2El) return;
        const step2Rect = step2El.getBoundingClientRect();
        const whyRect = whySectionEl?.getBoundingClientRect();
        let enter = step2Rect.top <= NAV_HEIGHT_PX && !isPricingTarget;
        const leave = isPricingTarget || (whyRect ? whyRect.bottom >= NAV_HEIGHT_PX + 4 : false);
        setSidebarMode((prev) => {
          if (enter && !prev) {
            anchorViewportTopRef.current = step2Rect.top;
            return true;
          }
          if (leave && prev) return false;
          return prev;
        });
      });
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    const ro = stepCardsGridRef.current ? new ResizeObserver(check) : null;
    stepCardsGridRef.current && ro?.observe(stepCardsGridRef.current);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
      ro?.disconnect();
    };
  }, [isMobile, isPricingTarget]);

  useEffect(() => {
    const t = setInterval(() => {
      setDemoIndex((i) => (i + 1) % CAROUSEL_LENGTH);
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  // Remove any ghost/injected nodes in the step-cards grid (e.g. clones without data-step-number).
  useEffect(() => {
    const grid = stepCardsGridRef.current;
    if (!grid) return;
    const removeGhosts = () => {
      const children = Array.from(grid.children);
      children.forEach((el) => {
        if (el.getAttribute("data-step-number") == null) {
          el.remove();
        }
      });
    };
    removeGhosts();
    const obs = new MutationObserver(removeGhosts);
    obs.observe(grid, { childList: true, subtree: false });
    return () => obs.disconnect();
  }, []);

  // Keep spacing below the final card so the cut section shape has breathing room.
  useEffect(() => {
    const grid = stepCardsGridRef.current;
    if (!grid) return;

    const update = () => {
      const step4El = grid.querySelector<HTMLElement>("[data-step-number='4']");
      if (step4El) {
        const step4Height = step4El.getBoundingClientRect().height;
        const next = Math.max(190, 190 + Math.round(0.9 * step4Height));
        setBottomSpacerMinHeight((prev) => (prev === next ? prev : next));
      }
    };

    update();
    window.addEventListener("resize", update);
    const ro = new ResizeObserver(update);
    ro.observe(grid);
    return () => {
      window.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, []);

  const visibleCardIndices = CARD_INDICES_BY_TRACK[track];

  // Step 1–4 cards. Each has data-step-number and data-card-slug. Track filters which are shown. Step 1 shows BCG image when BCG track.
  const cards: Array<{
    slug: string;
    stepLabel?: string;
    title: string;
    text: string;
    stickyText?: string;
    visual: React.ReactNode;
  }> = [
    {
      slug: "practice-the-real-thing",
      stepLabel: "Step 1",
      title: "Practice the real thing",
      text: "Start with the formats candidates actually care about. Sea Wolf, Red Rock, and Casey are front and center, so you can practise the real assessments instead of vague lookalikes, generic drills, and guesswork.",
      stickyText:
        "Start with the formats candidates actually care about. Sea Wolf, Red Rock, and Casey are front and center, so you can practise the real assessments instead of vague lookalikes and guesswork.",
      visual: <SeaWolfMock index={demoIndex} track={track} />,
    },
    {
      slug: "get-feedback-you-can-use",
      stepLabel: "Step 2",
      title: "Get feedback that actually helps",
      text: "Do not leave a session still wondering what went wrong. Cognidoor shows you where you lost points, why it happened, and what to change next, so each attempt teaches you something useful.",
      stickyText:
        "Do not leave a session still wondering what went wrong. Cognidoor shows you where you lost points, why it happened, and what to change next, so each attempt gets smarter.",
      visual: <CaseyMock index={demoIndex} track={track} />,
    },
    {
      slug: "case-library",
      stepLabel: "Step 3",
      title: "Build interview confidence with 24+ coached cases",
      text: "Start with easier cases, then move into harder ones as your confidence grows. The AI coach guides you through the case, shows you what strong thinking looks like, and gives clear feedback after each round so you know exactly what to improve next.",
      stickyText:
        "Start with easier cases, then move into harder ones as your confidence grows. The AI coach guides you through the case and gives clear feedback after each round so you know what to improve next.",
      visual: <DashboardMock index={demoIndex} track={track} />,
    },
    {
      slug: "see-your-progress-clearly",
      stepLabel: "Step 4",
      title: "See your progress clearly",
      text: "Improvement should feel visible. Your dashboard shows readiness, skill trends, momentum, and what to focus on next, so you can stop guessing whether you are getting better and start seeing it.",
      stickyText:
        "Improvement should feel visible. Your dashboard shows readiness, skill trends, and what to focus on next, so you can stop guessing and start seeing real progress.",
      visual: <DashboardSnapshotMock index={demoIndex} />,
    },
  ];

  // Smooth transitions: slightly snappier spring so layout animation (maxWidth/marginLeft) finishes sooner and feels less laggy
  const smoothTransition = {
    type: "spring" as const,
    stiffness: 140,
    damping: 26,
    mass: 0.8,
  };
  const fadeTransition = { duration: 0.52, ease: [0.25, 0.46, 0.45, 0.94] as const };
  const sidebarHeadlineTransition = { duration: 0.56, ease: [0.22, 0.61, 0.36, 1] as const };
  const containerShrink = showSidebar || exitDelayShrink;

  const platformHeadline =
    track === "mck"
      ? "One platform for McKinsey Assessment & Interview prep."
      : track === "bcg"
      ? "One platform for BCG Assessment & Interview prep."
      : "One platform for McKinsey and BCG Assessment & Interview prep.";

  const sectionEyebrow = "How Cognidoor gets you ready";
  const sectionTitle = "From first try to interview-ready";
  const sectionSubtitle = (
    <>
      Real assessments, useful feedback, coached cases, and progress you can actually see.
      <span className="mt-2 block text-white">{platformHeadline}</span>
    </>
  );

  const headlineBlock = (
    <>
      <div className="text-xs font-semibold uppercase tracking-wide text-white/70 md:text-sm">
        {sectionEyebrow}
      </div>
      <h2 className="mt-2 w-full font-black tracking-tight leading-[1.02]">
        <span className="block text-4xl md:text-5xl lg:text-[4.35rem]">
          {sectionTitle}
        </span>
        <span className="mt-3 block text-base text-white/80 md:text-lg max-w-xl">
          {sectionSubtitle}
        </span>
      </h2>
      <div className="mt-7 flex flex-wrap gap-3">
        <GradientButton to="/#live-demos">Preview a demo <Play size={16} /></GradientButton>
        <GhostButton to="/#pricing">Open pricing <ArrowRight size={16} /></GhostButton>
      </div>
    </>
  );

  // Mobile: static single-column layout, no sidebar or collapse animation; CTA at top and bottom.
  if (isMobile) {
    return (
      <SectionShell ref={sectionRef} track={track} noClipBottom>
        <div className="pb-24 sm:pb-28 pt-6 px-4 sm:px-6">
          <div className="w-full max-w-full text-center">
            {headlineBlock}
          </div>
          <div className="grid grid-cols-1 gap-6 mt-8 w-full">
            {cards.map((card, i) =>
              !visibleCardIndices.includes(i) ? null : (
                <div
                  key={`why-cognidoor-step-mobile-${i + 1}`}
                  data-step-number={String(i + 1)}
                  className="grid gap-4 rounded-2xl border border-white/10 bg-white/[0.035] p-4 sm:p-5 min-h-[280px] sm:min-h-[320px] w-full max-w-full min-w-0"
                >
                  <div className="min-w-0">
                    <div className="inline-flex items-center rounded-full border border-cyan-300/16 bg-cyan-300/7 px-3 py-1 text-xs font-semibold text-cyan-100">
                      {card.stepLabel ?? `Step ${i + 1}`}
                    </div>
                    <h3 className="mt-3 text-xl sm:text-2xl md:text-3xl font-black leading-tight text-white">{card.title}</h3>
                    <p className="mt-3 text-sm sm:text-base leading-relaxed text-white/80 max-w-prose">
                      {card.text}
                    </p>
                  </div>
                  <div className="min-h-0 w-full max-w-full min-w-0 rounded-xl">
                    {card.visual}
                  </div>
                </div>
              )
            )}
          </div>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <GradientButton to="/#live-demos">Preview a demo <Play size={16} /></GradientButton>
            <GhostButton to="/#pricing">Open pricing <ArrowRight size={16} /></GhostButton>
          </div>
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell ref={sectionRef} track={track} noClipBottom>
      <div className="pb-20 px-4 sm:px-8 md:px-16">
        <div
          ref={cardsColumnParentRef}
          className={cx(
            "grid gap-8 md:gap-12",
            showSidebar ? "grid-cols-1 lg:grid-cols-[380px_minmax(0,1fr)] lg:items-start" : "grid-cols-1"
          )}
        >
          {/* Left sidebar: sticky when Step 2 hits nav on lg+ (clip-path on SectionShell breaks sticky — use noClipBottom) */}
          {showSidebar && (
            <motion.div
              className="sticky self-start pt-6"
              style={{ top: NAV_HEIGHT_PX }}
              initial={{ opacity: 0, y: 10 }}
              animate={{
                opacity: showSidebarHeadline ? 1 : 0,
                y: showSidebarHeadline ? 0 : 10,
              }}
              transition={sidebarHeadlineTransition}
            >
              <div className="w-full min-w-0 text-left">
                {headlineBlock}
              </div>
            </motion.div>
          )}

          <motion.div
            className="min-w-0 flex flex-col w-full"
            animate={
              isXl
                ? containerShrink
                  ? {
                      maxWidth: expandedLayoutPx.compactMaxWidth,
                      marginLeft: showSidebar
                        ? expandedLayoutPx.compactMarginLeft
                        : expandedLayoutPx.expandedMarginLeft + expandedLayoutPx.expandedMaxWidth - expandedLayoutPx.compactMaxWidth,
                      marginRight: 0,
                    }
                  : {
                      maxWidth: expandedLayoutPx.expandedMaxWidth,
                      marginLeft: expandedLayoutPx.expandedMarginLeft,
                      marginRight: 0,
                    }
                : { maxWidth: "100%", marginLeft: 0, marginRight: 0 }
            }
            transition={smoothTransition}
            style={{ width: "100%" }}
          >
            {/* Center: headline above cards when not in sidebar mode; fades out as sidebar appears */}
            {!showSidebar && (
              <motion.div
                className="-mx-4 px-4 sm:-mx-8 sm:px-8 md:-mx-16 md:px-16 pt-8 md:pt-10 pb-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={fadeTransition}
              >
                <div className="w-full min-w-0 max-w-full text-center">
                  <div className="text-xs font-semibold uppercase tracking-wide text-white/70 md:text-sm">
                    {sectionEyebrow}
                  </div>
                  <h2 className="mt-2 w-full font-black tracking-tight leading-[1.02]">
                    <span className="block text-4xl md:text-5xl lg:text-[4.35rem]">
                      {sectionTitle}
                    </span>
                    <span className="mt-3 block text-base text-white/80 md:text-lg max-w-3xl mx-auto">
                      {sectionSubtitle}
                    </span>
                  </h2>
                </div>
                <div className="mt-7 flex flex-wrap gap-3 ml-auto max-w-7xl w-full justify-start">
                  <GradientButton to="/#live-demos">Preview a demo <Play size={16} /></GradientButton>
                  <GhostButton to="/#pricing">Open pricing <ArrowRight size={16} /></GhostButton>
                </div>
              </motion.div>
            )}

            <div
              ref={stepCardsGridRef}
              className="grid grid-cols-1 gap-6 mt-8 w-full"
              style={{ gridAutoFlow: "row" }}
            >
              {cards.map((card, i) =>
                !visibleCardIndices.includes(i) ? null : (
                  <div
                    key={`why-cognidoor-step-${i + 1}`}
                    ref={i === 0 ? step1CardRef : undefined}
                    data-step-number={i + 1}
                    data-card-slug={card.slug}
                    className={cx(
                      "grid gap-6 rounded-[30px] border border-white/10 bg-white/[0.035] p-5 md:p-6 lg:grid-cols-[380px_minmax(0,1fr)] lg:items-start",
                      (i === 2 || i === 3) && "min-h-[760px]"
                    )}
                    style={{ gridRow: i + 1, order: i + 1 }}
                  >
                    <div>
                      <div className="inline-flex items-center rounded-full border border-cyan-300/16 bg-cyan-300/7 px-3 py-1 text-xs font-semibold text-cyan-100">
                        {card.stepLabel ?? `Step ${i + 1}`}
                      </div>
                      <h3 className="mt-4 text-3xl md:text-4xl font-black leading-tight text-white">{card.title}</h3>
                      <p className="mt-4 text-base md:text-[0.95rem] leading-relaxed text-white/80 max-w-prose">
                        {showSidebar ? (card.stickyText ?? card.text) : card.text}
                      </p>
                    </div>
                    <div>{card.visual}</div>
                  </div>
                )
              )}
            </div>
            <div className="mt-8 shrink-0" style={{ minHeight: bottomSpacerMinHeight }} aria-hidden />
          </motion.div>
        </div>
      </div>
    </SectionShell>
  );
}
